import React, { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import { distributorService } from "../services/apiService";
import { useProfile } from "../utils/UseProfile";
import Toast from "../components/Toast";
import { API_BASE_URL, API_PREFIX, API_ENDPOINTS } from "../constants/config";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_URL = `${API_BASE_URL}${API_PREFIX}`;
const SS = "dist_search";
const ss = () => { try { return JSON.parse(sessionStorage.getItem(SS)) || {}; } catch { return {}; } };
const saveSession = (patch) => { try { sessionStorage.setItem(SS, JSON.stringify({ ...ss(), ...patch })); } catch {} };

// ─── Inline editable cell ─────────────────────────────────────────────────────
const EditCell = ({ editing, type = "text", name, value, onChange, placeholder, display }) => {
  if (!editing) return <span>{display ?? value ?? "—"}</span>;
  return (
    <input type={type} name={name} value={value} onChange={onChange}
      placeholder={placeholder}
      className="w-full px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none" />
  );
};

const SortIcon = ({ col, sortConfig }) => {
  if (!sortConfig || sortConfig.key !== col) return <i className="fas fa-sort text-gray-300 ml-1 text-xs" />;
  return sortConfig.dir === "asc" ? <i className="fas fa-sort-up text-indigo-500 ml-1 text-xs" /> : <i className="fas fa-sort-down text-indigo-500 ml-1 text-xs" />;
};

const Th = ({ col, label, className = "text-left", sortConfig, onSort }) => (
  <th className={`py-2 px-4 ${className} cursor-pointer select-none hover:bg-blue-100`} onClick={() => onSort(col)}>
    {label}<SortIcon col={col} sortConfig={sortConfig} />
  </th>
);

const DistributorSearch = () => {
  const { profile } = useProfile();
  const isAdmin = profile?.roleResponse?.role?.toLowerCase().includes("admin");

  const location = useLocation();

  // Only restore session if it's a browser refresh (not navigation)
  const isRefresh = () => sessionStorage.getItem(SS + "_refresh") === "1";

  const [searchName,   setSearchNameState]   = useState(() => isRefresh() ? (ss().searchName   || "") : "");
  const [searchMobile, setSearchMobileState] = useState(() => isRefresh() ? (ss().searchMobile || "") : "");
  const [searchStatus, setSearchStatusState] = useState(() => isRefresh() ? (ss().searchStatus || "") : "");
  const [results,      setResultsState]      = useState(() => isRefresh() ? (ss().results      || []) : []);
  const [searched,     setSearchedState]     = useState(() => isRefresh() ? (ss().searched     || false) : false);
  const [page,         setPageState]         = useState(() => isRefresh() ? (ss().page         || 0) : 0);

  const setSearchName   = (v) => { setSearchNameState(v);   saveSession({ searchName: v }); };
  const setSearchMobile = (v) => { setSearchMobileState(v); saveSession({ searchMobile: v }); };
  const setSearchStatus = (v) => { setSearchStatusState(v); saveSession({ searchStatus: v }); };
  const setResults      = (v) => { setResultsState(v);      saveSession({ results: v }); };
  const setSearched     = (v) => { setSearchedState(v);     saveSession({ searched: v }); };
  const setPage         = (v) => { const val = typeof v === "function" ? v(page) : v; setPageState(val); saveSession({ page: val }); };

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const PAGE_SIZE = 10;
  const [editMap, setEditMap] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
  const handleSort = (key) => setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedResults = [...results].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] ?? "";
    const bVal = b[sortConfig.key] ?? "";
    const cmp = typeof aVal === "number" && typeof bVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortConfig.dir === "asc" ? cmp : -cmp;
  });

  useEffect(() => {
    // Mark as refresh when browser reloads (beforeunload fires before unload)
    const onUnload = () => sessionStorage.setItem(SS + "_refresh", "1");
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      // If navigating away (not a refresh), clear session data
      if (!sessionStorage.getItem(SS + "_refresh")) {
        sessionStorage.removeItem(SS);
      }
      // Always clear the refresh flag after reading it
      sessionStorage.removeItem(SS + "_refresh");
    };
  }, []);

  const showMsg = (type, text, identifier = null) => {
    setToast({ type, identifier, message: text.replace(/^[✅⚠️❌]\s*/, "") });
  };

  const downloadReport = async (type) => {
    try {
      const endpoint = type === "pdf" ? API_ENDPOINTS.DISTRIBUTOR_REPORT_PDF : API_ENDPOINTS.DISTRIBUTOR_REPORT_EXCEL;
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-Authenticated-User": localStorage.getItem("userName") || "",
        },
        body: JSON.stringify({
          distributorName: searchName.trim() || null,
          contactNumber: searchMobile.trim() || null,
          status: searchStatus || null,
          page: 0, size: 1000,
        }),
      });
      if (!res.ok) throw new Error("Failed to generate report");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `distributor_report_${new Date().toISOString().slice(0,10)}.${type === "pdf" ? "pdf" : "xlsx"}`;
      link.click();
    } catch { showMsg("error", "Failed to download report"); }
  };

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setSearched(true);
    try {
      const data = await distributorService.searchDistributors({
        distributorName: searchName.trim(),
        contactNumber: searchMobile.trim(),
        status: searchStatus || null,
        page: 0, size: 50,
      });
      const list = data.content ?? [];
      setResults(list);
      setPage(0);
      if (!list.length) showMsg("error", "No distributors found for the given criteria");
    } catch (err) {
      showMsg("error", err.response?.data?.errorMessage || "Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchName, searchMobile, searchStatus]);

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };

  const startEdit = (distributor) => {
    setEditMap((prev) => ({
      ...prev,
      [distributor.distributorId]: {
        distributorName: distributor.distributorName || "",
        contactNumber: distributor.contactNumber || "",
        email: distributor.email || "",
        address: distributor.address || "",
        status: distributor.status || "ACTIVE",
      },
    }));
  };

  const cancelEdit = (distributorId) => {
    setEditMap((prev) => { const n = { ...prev }; delete n[distributorId]; return n; });
  };

  const handleEditChange = (distributorId, e) => {
    const { name, value } = e.target;
    setEditMap((prev) => ({ ...prev, [distributorId]: { ...prev[distributorId], [name]: value } }));
  };

  const handleSave = async (distributorId) => {
    const data = editMap[distributorId];
    setSavingId(distributorId);
    try {
      await distributorService.updateDistributor({ distributorId, distributorName: data.distributorName, contactNumber: data.contactNumber, email: data.email, address: data.address, status: data.status });
      setResults(results.map((r) => r.distributorId === distributorId ? { ...r, ...data } : r));
      cancelEdit(distributorId);
      showMsg("success", "Distributor updated successfully", `Distributor: ${data.distributorName}`);
    } catch (err) {
      showMsg("error", err.response?.data?.errorMessage || "Update failed", `Distributor: ${editMap[distributorId]?.distributorName}`);
    } finally { setSavingId(null); }
  };

  const handleDelete = async (distributorId, distributorName) => {
    if (!window.confirm(`Delete distributor "${distributorName}"?`)) return;
    setDeletingId(distributorId);
    try {
      await distributorService.deleteDistributor(distributorId);
      setResults(results.filter((r) => r.distributorId !== distributorId));
      showMsg("success", "Distributor deleted", `Distributor: ${distributorName}`);
    } catch (err) {
      showMsg("error", err.response?.data?.errorMessage || "Delete failed", `Distributor: ${distributorName}`);
    } finally { setDeletingId(null); }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} identifier={toast.identifier} message={toast.message} onClose={() => setToast(null)} />}

      {/* Search filters */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          🔍 Search Distributors
        </h2>
        <div className="p-2">
          <div className="flex flex-wrap gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Distributor Name</label>
              <input type="text" value={searchName} onKeyDown={handleKeyDown}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="e.g. ABC Pharma"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Number</label>
              <input type="tel" value={searchMobile} onKeyDown={handleKeyDown} maxLength="10"
                onChange={(e) => setSearchMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="e.g. 9876543210"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={searchStatus} onChange={(e) => setSearchStatus(e.target.value)}
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none">
                <option value="">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setSearchNameState(""); setSearchMobileState(""); setSearchStatusState(""); setResultsState([]); setSearchedState(false); setPageState(0); sessionStorage.removeItem(SS); }}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
              <i className="fas fa-redo" /> Reset
            </button>
            <button onClick={handleSearch} disabled={loading}
              className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
              {loading ? <><i className="fas fa-spinner fa-spin" />Searching...</> : <><i className="fas fa-search" />Search</>}
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          <h2 className="text-base font-bold text-blue-800">
            📋 Distributor Details
            {results.length > 0 && <span className="text-xs font-normal text-blue-600 ml-2">({results.length} distributor{results.length !== 1 ? "s" : ""})</span>}
          </h2>
          {results.length > 0 && (
            <div className="flex gap-2">
              <button onClick={() => downloadReport("pdf")} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow transition">
                <i className="fas fa-file-pdf" /> PDF
              </button>
              <button onClick={() => downloadReport("excel")} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-semibold shadow transition">
                <i className="fas fa-file-excel" /> Excel
              </button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-800">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide">
              <tr>
                <Th col="distributorName" label="Name" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="contactNumber" label="Mobile No" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="email" label="Email" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="address" label="Address" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="status" label="Status" sortConfig={sortConfig} onSort={handleSort} />
                {isAdmin && <th className="py-2 px-4 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isAdmin ? 6 : 5} className="text-center py-6"><i className="fas fa-spinner fa-spin text-blue-400 text-2xl" /></td></tr>
              ) : results.length === 0 ? (
                <tr><td colSpan={isAdmin ? 6 : 5} className="py-10 text-center text-gray-400">
                  <i className={`fas ${searched ? "fa-inbox" : "fa-search"} text-4xl mb-3 block`} />
                  <p className="text-sm">{searched ? "No distributors found. Try different search criteria." : "Use the search above to find distributors."}</p>
                </td></tr>
              ) : (
                sortedResults.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((r, idx) => {
                  const editing = !!editMap[r.distributorId];
                  const ed = editMap[r.distributorId] || {};
                  return (
                    <tr key={r.distributorId} className={`border-t transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>
                      <td className="py-1 px-4 font-medium"><EditCell editing={editing} name="distributorName" value={ed.distributorName} onChange={(e) => handleEditChange(r.distributorId, e)} display={r.distributorName} /></td>
                      <td className="py-1 px-4"><EditCell editing={editing} type="tel" name="contactNumber" value={ed.contactNumber} onChange={(e) => handleEditChange(r.distributorId, e)} display={r.contactNumber} /></td>
                      <td className="py-1 px-4"><EditCell editing={editing} type="email" name="email" value={ed.email} onChange={(e) => handleEditChange(r.distributorId, e)} display={r.email || "—"} /></td>
                      <td className="py-1 px-4"><EditCell editing={editing} name="address" value={ed.address} onChange={(e) => handleEditChange(r.distributorId, e)} display={r.address} /></td>
                      <td className="py-1 px-4">
                        {editing ? (
                          <select name="status" value={ed.status} onChange={(e) => handleEditChange(r.distributorId, e)}
                            className="w-full px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none">
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="INACTIVE">INACTIVE</option>
                          </select>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{r.status}</span>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="py-1 px-4">
                          <div className="flex items-center justify-center gap-2">
                            {editing ? (
                              <>
                                <button onClick={() => handleSave(r.distributorId)} disabled={savingId === r.distributorId} title="Save" className="text-green-600 hover:text-green-800 disabled:opacity-50">
                                  {savingId === r.distributorId ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                                </button>
                                <button onClick={() => cancelEdit(r.distributorId)} title="Cancel" className="text-gray-500 hover:text-gray-700"><i className="fas fa-times" /></button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => startEdit(r)} title="Edit" className="text-blue-500 hover:text-blue-700"><i className="fas fa-edit" /></button>
                                <button onClick={() => handleDelete(r.distributorId, r.distributorName)} disabled={deletingId === r.distributorId} title="Delete" className="text-red-500 hover:text-red-700 disabled:opacity-50">
                                  {deletingId === r.distributorId ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-trash-alt" />}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {results.length > 0 && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white">
            <span className="text-xs text-gray-500">Showing {Math.min(page * PAGE_SIZE + 1, results.length)}–{Math.min((page + 1) * PAGE_SIZE, results.length)} of {results.length}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(0)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: Math.ceil(results.length / PAGE_SIZE) }, (_, i) => (
                <button key={i} onClick={() => setPage(i)} className={`px-2 py-1 text-xs rounded border ${i === page ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}>{i + 1}</button>
              ))}
              <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(results.length / PAGE_SIZE) - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => setPage(Math.ceil(results.length / PAGE_SIZE) - 1)} disabled={page >= Math.ceil(results.length / PAGE_SIZE) - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default DistributorSearch;
